import { createSelector } from 'reselect';
import { find, pathOr, propEq } from 'ramda';
import { CurrentCustomerSelector, SelectedCustomerSelector } from './customer.selectors';
import { MetadataConstants, MetadataSelectors } from 'invision-core';
import { addressStateRegionProvinceValueOptionsForCountry } from '../helpers/customer.selectors.helpers';
import { ADDRESS_STATUSES } from '../../components/customer/addresses/addresses.constants';
import Immutable from 'seamless-immutable';
var EMPTY_ARRAY = Immutable([]);
var recoverableUIStateSelector = function recoverableUIStateSelector(state) {
  return state.customercare.recoverableUiState.addresses;
};
export var SelectedItemIdSelector = createSelector([recoverableUIStateSelector], function (uiState) {
  return uiState.selectedItemId;
});
export var AddressDataSelector = createSelector([SelectedCustomerSelector], function (customer) {
  return customer.addresses.addressData;
});
export var CurrentFormAddressSelector = createSelector([SelectedCustomerSelector], function (customer) {
  return customer.addresses.currentFormAddress;
});
export var CreateAddressPopupStateRegionProvinceValueOptions = createSelector([AddressDataSelector, MetadataSelectors.codes.MetadataCodeTypeSelector(MetadataConstants.codes.AddressCountry), MetadataSelectors.codes.MetadataCodeTypeSelector(MetadataConstants.codes.AddressStateProvinceRegion)], function (addressData, countryCodes, stateCodes) {
  return addressData.address ? addressStateRegionProvinceValueOptionsForCountry(addressData.address.Country, countryCodes, stateCodes) : null;
});
export var UpdateAddressPopupStateRegionProvinceValueOptions = createSelector([AddressDataSelector, MetadataSelectors.codes.MetadataCodeTypeSelector(MetadataConstants.codes.AddressCountry), MetadataSelectors.codes.MetadataCodeTypeSelector(MetadataConstants.codes.AddressStateProvinceRegion)], function (addressData, countryCodes, stateCodes) {
  return addressData.address ? addressStateRegionProvinceValueOptionsForCountry(addressData.address.Country, countryCodes, stateCodes) : null;
});
export var CurrentAddressesSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return pathOr(EMPTY_ARRAY, ['addresses', 'data'], selectedCustomer);
});
export var ShippingAddressesSelector = createSelector([CurrentAddressesSelector], function (addresses) {
  return addresses.find(function (address) {
    return address.DefaultShipping;
  });
});
export var PreventOverrideFlagSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return pathOr(null, ['addresses', 'preventOverrideFlag'], selectedCustomer);
});
export var SelectedAddressSelector = createSelector([SelectedItemIdSelector, CurrentAddressesSelector], function (selectedItemId, addresses) {
  if (addresses && addresses.length) {
    if (selectedItemId) {
      return find(propEq(selectedItemId, 'Id'))(addresses) || null;
    } else if (!selectedItemId) {
      return addresses[0].Id;
    } else {
      return null;
    }
  }
  return null;
});
var DEFAULT_ADDRESS_TYPES = {
  home: 'DefaultHome',
  postal: 'DefaultPostal',
  service: 'DefaultService',
  work: 'DefaultWork'
};
var DEFAULT_ACTIVE_ADDRESSES = [];
export var CurrentActiveAddressesSelector = createSelector([CurrentAddressesSelector], function (addresses) {
  return Array.isArray(addresses) ? addresses.filter(function (address) {
    return address.Status === ADDRESS_STATUSES.ACTIVE;
  }) : DEFAULT_ACTIVE_ADDRESSES;
});
var getAddressDefaultByTypeId = function getAddressDefaultByTypeId(currentCustomer, type, addresses) {
  var defaultAddress = null;
  if (addresses.length) {
    defaultAddress = pathOr(null, ['ConvergentBillerId'], currentCustomer) ? find(propEq(true, type), addresses) : undefined;
  }
  return pathOr(null, ['Id'], defaultAddress);
};
export var PreventRemovalOfDefaultTypesSelector = createSelector([CurrentCustomerSelector, CurrentActiveAddressesSelector, CurrentFormAddressSelector], function (currentCustomer, currentActiveAddresses, currentFormAddress) {
  var isSelectedAddressDefaultByType = function isSelectedAddressDefaultByType(type) {
    return currentCustomer.ConvergentBillerId ? pathOr(false, [type], currentFormAddress) : false;
  };

  // checks to see if an address default exists on other accounts or has been selected in the current form
  var getDefaultAddressExists = function getDefaultAddressExists(currentAddress, id) {
    if (currentAddress) {
      return true;
    } else {
      return id ? id !== pathOr(false, ['Id'], currentFormAddress) : false;
    }
  };

  // Is selected address default
  var isSelectedAddressDefaultHome = isSelectedAddressDefaultByType(DEFAULT_ADDRESS_TYPES.home);
  var isSelectedAddressDefaultPostal = isSelectedAddressDefaultByType(DEFAULT_ADDRESS_TYPES.postal);
  var isSelectedAddressDefaultService = isSelectedAddressDefaultByType(DEFAULT_ADDRESS_TYPES.service);
  var isSelectedAddressDefaultWork = isSelectedAddressDefaultByType(DEFAULT_ADDRESS_TYPES.work);

  // the default address id
  var defaultHomeAddressId = getAddressDefaultByTypeId(currentCustomer, DEFAULT_ADDRESS_TYPES.home, currentActiveAddresses);
  var defaultPostalAddressId = getAddressDefaultByTypeId(currentCustomer, DEFAULT_ADDRESS_TYPES.postal, currentActiveAddresses);
  var defaultServiceAddressId = getAddressDefaultByTypeId(currentCustomer, DEFAULT_ADDRESS_TYPES.service, currentActiveAddresses);
  var defaultWorkAddressId = getAddressDefaultByTypeId(currentCustomer, DEFAULT_ADDRESS_TYPES.work, currentActiveAddresses);

  // the updated address including if selected in the form or on other addresses
  var updatedDefaultHomeAddressExists = getDefaultAddressExists(isSelectedAddressDefaultHome, defaultHomeAddressId);
  var updatedDefaultPostalAddressExists = getDefaultAddressExists(isSelectedAddressDefaultPostal, defaultPostalAddressId);
  var updatedDefaultWorkAddressExists = getDefaultAddressExists(isSelectedAddressDefaultWork, defaultWorkAddressId);
  var atLeastTwoConvergentBillerAddressExists = updatedDefaultHomeAddressExists && updatedDefaultPostalAddressExists || updatedDefaultHomeAddressExists && updatedDefaultWorkAddressExists || updatedDefaultPostalAddressExists && updatedDefaultWorkAddressExists;

  // If atLeastTwoConvergentBillerAddressExists always return false. Then Determine if there is an existing
  // addresses with defaults and it's not the current one selected, Otherwise disable it if it is selected,
  // unless they don't have a convergent biller id then never disable it
  var isDefaultTypePreventedFromRemoval = function isDefaultTypePreventedFromRemoval(currentAddress, id) {
    if (atLeastTwoConvergentBillerAddressExists) {
      return false;
    } else if (id !== null && currentAddress && currentCustomer.ConvergentBillerId) {
      return id === pathOr(false, ['Id'], currentFormAddress);
    } else {
      return currentAddress;
    }
  };
  return {
    home: isDefaultTypePreventedFromRemoval(isSelectedAddressDefaultHome, defaultHomeAddressId),
    postal: isDefaultTypePreventedFromRemoval(isSelectedAddressDefaultPostal, defaultPostalAddressId),
    service: isSelectedAddressDefaultService && defaultServiceAddressId === currentFormAddress.Id,
    work: isDefaultTypePreventedFromRemoval(isSelectedAddressDefaultWork, defaultWorkAddressId)
  };
});
export var IsSelectedAddressRemovableSelector = createSelector([SelectedAddressSelector, CurrentCustomerSelector, CurrentActiveAddressesSelector], function (selectedAddress, currentCustomer, currentActiveAddresses) {
  var homeDefaultAddressId = getAddressDefaultByTypeId(currentCustomer, DEFAULT_ADDRESS_TYPES.home, currentActiveAddresses);
  var postalDefaultAddressId = getAddressDefaultByTypeId(currentCustomer, DEFAULT_ADDRESS_TYPES.postal, currentActiveAddresses);
  var workDefaultAddressId = getAddressDefaultByTypeId(currentCustomer, DEFAULT_ADDRESS_TYPES.work, currentActiveAddresses);
  var isOnlyConvergentBillerAddress = function isOnlyConvergentBillerAddress() {
    if (selectedAddress.DefaultHome) {
      return (workDefaultAddressId === null || workDefaultAddressId === selectedAddress.Id) && (postalDefaultAddressId === null || postalDefaultAddressId === selectedAddress.Id);
    } else if (selectedAddress.DefaultPostal) {
      return (workDefaultAddressId === null || workDefaultAddressId === selectedAddress.Id) && (homeDefaultAddressId === null || homeDefaultAddressId === selectedAddress.Id);
    } else if (selectedAddress.DefaultWork) {
      return (homeDefaultAddressId === null || homeDefaultAddressId === selectedAddress.Id) && (postalDefaultAddressId === null || postalDefaultAddressId === selectedAddress.Id);
    }
  };
  var isAddressRemovable = function isAddressRemovable() {
    if (selectedAddress && pathOr(null, ['ConvergentBillerId'], currentCustomer)) {
      return selectedAddress.InUse ? !selectedAddress.InUse : selectedAddress.DefaultService ? !selectedAddress.DefaultService : !isOnlyConvergentBillerAddress();
    } else {
      return true;
    }
  };
  return isAddressRemovable();
});
export var IsFetchingAddressesSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.addresses.isFetchingAddresses;
});
export var IsRemovingAddressSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.addresses.isRemovingAddress;
});
export var IsCreatingAddressSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.addresses.isCreatingAddress;
});
export var PostalCodeSelector = createSelector([AddressDataSelector, MetadataSelectors.codes.MetadataCodeTypeSelector(MetadataConstants.codes.AddressCountry)], function (addressData, codes) {
  var addressCountry = pathOr('', ['address', 'Country'], addressData);
  if (addressCountry && codes.length > 0) {
    var country = find(propEq(addressCountry, 'Value'))(codes);
    if (country) {
      var regex = find(propEq('zip_code_reg_expression', 'Key'))(country.AdditionalProperties).Value;
      var mask = find(propEq('zip_code_mask', 'Key'))(country.AdditionalProperties).Value;
      return {
        regEx: new RegExp(regex),
        mask: mask
      };
    }
  }
  return {
    regEx: '',
    mask: ''
  };
});
export var IsUpdatingAddressSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.addresses.isUpdatingAddress;
});
export var AddressesErrorSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.addresses.addressesError ? [selectedCustomer.addresses.addressesError.translatedMessage] : [];
});
export var RemoveAddressesErrorSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.addresses.removeAddressesError ? selectedCustomer.addresses.removeAddressesError.translatedMessage : null;
});
export var UpdateAddressErrorSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.addresses.updateAddressError ? [selectedCustomer.addresses.updateAddressError.translatedMessage] : [];
});
export var DefaultBillingAddressSelector = createSelector([CurrentAddressesSelector], function (addresses) {
  if (!addresses.length) {
    return null;
  }
  var defaultBilling = propEq(true, 'DefaultBilling');
  return find(defaultBilling, addresses);
});
export var DefaultShippingAddressSelector = createSelector([CurrentActiveAddressesSelector], function () {
  var addresses = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  if (!addresses) {
    return null;
  }
  var defaultShipping = propEq(true, 'DefaultShipping');
  return find(defaultShipping, addresses);
});
export var IncludeRemovedSelector = createSelector([recoverableUIStateSelector], function (uiState) {
  return uiState.filterData.includeRemoved;
});